import {
  appleTrackIdFrom,
  normalizeSongIdentity,
  spotifyTrackIdFrom,
  tidalTrackIdFrom,
  type CatalogIds,
  type SongIdentityInput,
} from '../utils/songIdentity';

export type PlayMode = 'spotify_manual' | 'apple_manual' | 'tidal_manual' | 'apple_automated' | 'tidal_virtualdj';
export type PlayerProvider = 'spotify' | 'apple' | 'tidal';
export type SearchCatalog = 'apple_music' | 'itunes';

export const SELECTABLE_PLAY_MODES: PlayMode[] = ['spotify_manual', 'apple_manual', 'tidal_manual'];
export const LEGACY_PLAY_MODE: PlayMode = 'spotify_manual';

export interface PlayModeConfig {
  id: PlayMode;
  name: string;
  playerName: string;
  provider: PlayerProvider;
  selectable: boolean;
  automatic: boolean;
  description: string;
  searchCatalog: SearchCatalog;
}

const PLAY_MODE_CONFIGS: Record<PlayMode, PlayModeConfig> = {
  spotify_manual: {
    id: 'spotify_manual',
    name: 'Spotify (manual)',
    playerName: 'Spotify',
    provider: 'spotify',
    selectable: true,
    automatic: false,
    description: 'You play each song in Spotify. Your buttons open the exact track in the Spotify app.',
    searchCatalog: 'apple_music',
  },
  apple_manual: {
    id: 'apple_manual',
    name: 'Apple Music (manual)',
    playerName: 'Apple Music',
    provider: 'apple',
    selectable: true,
    automatic: false,
    description: 'You play each song in Apple Music. Songs guests pick already carry their Apple Music ID.',
    searchCatalog: 'apple_music',
  },
  tidal_manual: {
    id: 'tidal_manual',
    name: 'TIDAL (manual)',
    playerName: 'TIDAL',
    provider: 'tidal',
    selectable: true,
    automatic: false,
    description: 'You play each song in TIDAL. Paste a TIDAL link to match each song.',
    searchCatalog: 'itunes',
  },
  apple_automated: {
    id: 'apple_automated',
    name: 'Apple Music (automatic)',
    playerName: 'Apple Music',
    provider: 'apple',
    selectable: false,
    automatic: true,
    description: 'VoteBeats plays the queue in Apple Music for you.',
    searchCatalog: 'apple_music',
  },
  tidal_virtualdj: {
    id: 'tidal_virtualdj',
    name: 'TIDAL + VirtualDJ',
    playerName: 'TIDAL',
    provider: 'tidal',
    selectable: false,
    automatic: true,
    description: 'VoteBeats loads the queue into VirtualDJ through TIDAL.',
    searchCatalog: 'itunes',
  },
};

export const ALL_PLAY_MODES: PlayMode[] = Object.keys(PLAY_MODE_CONFIGS) as PlayMode[];

export function isPlayMode(value: unknown): value is PlayMode {
  return typeof value === 'string' && value in PLAY_MODE_CONFIGS;
}

export function playModeConfig(mode: PlayMode): PlayModeConfig {
  return PLAY_MODE_CONFIGS[mode];
}

export function playModeOf(
  event: { settings?: { playMode?: PlayMode } | null } | null | undefined,
): PlayModeConfig {
  const mode = event?.settings?.playMode;
  return PLAY_MODE_CONFIGS[isPlayMode(mode) ? mode : LEGACY_PLAY_MODE];
}

export function modeTakesPastedLinks(mode: PlayModeConfig): boolean {
  return !mode.automatic;
}

export function searchCatalogOf(event: Parameters<typeof playModeOf>[0]): SearchCatalog {
  return playModeOf(event).searchCatalog;
}

export interface PlayableRequest {
  song: SongIdentityInput['song'] & { title: string; artist: string };
  platformLink?: string | null;
  platformName?: string | null;
  spotifyUri?: string | null;
  localFile?: unknown;
}

export function catalogIdsOf(request: PlayableRequest): CatalogIds {
  return normalizeSongIdentity({
    song: request.song,
    platformLink: request.platformLink,
    platformName: request.platformName,
    spotifyUri: request.spotifyUri,
  }).catalogIds;
}

export function trackIdForMode(request: PlayableRequest, mode: PlayModeConfig): string | null {
  return catalogIdsOf(request)[mode.provider] ?? null;
}

export function songFitsPlayMode(request: PlayableRequest, mode: PlayModeConfig): boolean {
  if (request.localFile) return true;
  return trackIdForMode(request, mode) !== null;
}

export function needsLinkMatch(request: PlayableRequest, mode: PlayModeConfig): boolean {
  return !songFitsPlayMode(request, mode);
}

export function linkFitsPlayMode(
  platformLink: string | null | undefined,
  _platformName: string | null | undefined,
  mode: PlayModeConfig,
): boolean {
  if (!platformLink) return false;
  if (mode.provider === 'spotify') return spotifyTrackIdFrom(platformLink) !== null;
  if (mode.provider === 'tidal') return tidalTrackIdFrom(platformLink) !== null;
  return appleTrackIdFrom(platformLink) !== null;
}

export function exactUrlFor(trackId: string, mode: PlayModeConfig): string {
  if (mode.provider === 'spotify') return `spotify:track:${trackId}`;
  if (mode.provider === 'tidal') return `https://tidal.com/browse/track/${trackId}`;
  return `music://music.apple.com/us/song/${trackId}`;
}

export function searchUrlFor(title: string, artist: string, mode: PlayModeConfig): string {
  const query = encodeURIComponent(`${title} ${artist}`);
  if (mode.provider === 'spotify') return `spotify:search:${query}`;
  if (mode.provider === 'tidal') return `https://tidal.com/search?q=${query}`;
  return `music://music.apple.com/search?term=${query}`;
}

export function openUrlFor(request: PlayableRequest, mode: PlayModeConfig): { url: string; isDirect: boolean } {
  const trackId = trackIdForMode(request, mode);
  if (trackId) return { url: exactUrlFor(trackId, mode), isDirect: true };
  return { url: searchUrlFor(request.song.title, request.song.artist, mode), isDirect: false };
}

export function playerNameMatchesMode(detectedPlayerName: string | null | undefined, mode: PlayModeConfig): boolean {
  if (!detectedPlayerName) return true;
  return detectedPlayerName.trim().toLowerCase() === mode.playerName.toLowerCase();
}

export function modeLinkFrom(
  links: { spotify?: string; appleMusic?: string; tidal?: string } | null | undefined,
  mode: PlayModeConfig,
): string | null {
  if (!links) return null;
  const link = mode.provider === 'spotify' ? links.spotify : mode.provider === 'tidal' ? links.tidal : links.appleMusic;
  return link && linkFitsPlayMode(link, mode.playerName, mode) ? link : null;
}
